import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { Button, Card, CardBody, CardHeader, Col, Collapse, Container, Form, Row } from "react-bootstrap";
import { Tabulator, PageModule, DownloadModule, ExportModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import { SingleSelectAsync } from "./SIngleSelectAsync";
import { SelectOption } from "./SIngleSelectAsync";
import { prodApiEndpointUrl, stagingApiEndpointUrl } from 'types/apiEndpoints';

interface AllEntity {
  name: string;
  id: number;
}

interface HighestPaidEmployeeByEntity {
  entity_id: number;
  emp_rank: number;
  fiscal_year: number;
  entity_name: string;
  employee_name: string;
  title: string;
  org1: string;
  wages: number;
  benefits: number;
  total: number;
}

interface EntityHighestPaidTableStandaloneProps {
  selectedEntityId: number;
  formatCurrency: (value: number) => string;
  formatEntityString: (value: string) => string;
  data: HighestPaidEmployeeByEntity[];
  loading?: boolean;
  entityName?: string;
  fiscalYear: string | null;
  onFiscalYearChange?: (year: string | null) => void;
  onEntityChange?: (entityId: number) => void;
}

const EntityHighestPaidTableStandalone: React.FC<
  EntityHighestPaidTableStandaloneProps
> = ({
  selectedEntityId,
  formatCurrency,
  formatEntityString,
  data,
  loading,
  entityName,
  fiscalYear,
  onFiscalYearChange,
  onEntityChange,
}) => {
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
  const [allEntities, setAllEntities] = useState<AllEntity[]>([]);
  const [allFiscalYears, setAllFiscalYears] = useState<string[]>([]);
  const [entity, setEntity] = useState<AllEntity | null>(null);
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
  const [apiEndpoint] = useState<string>(() => {
    return window.location.hostname === "transparent.utah.gov" ? prodApiEndpointUrl : stagingApiEndpointUrl;
  });
  const entityOptions = React.useMemo(
    () => allEntities.map(e => ({ value: e.name, label: e.name })),
    [allEntities]
  );
  const [selectedYear, setSelectedYear] = useState<SelectOption | null>(null);
  const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const selectedEntityOption = React.useMemo(() => {
    if (!entity) return null;
    return entityOptions.find(option => option.value === entity.name) ?? null;
  }, [entity, entityOptions]);

  const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {
    if (!tabulatorRef.current) return;
    const formattedEntityName = entity?.name?.toLowerCase().replaceAll(" ", "_") || "entity";
    tabulatorRef.current.download(type, `${formattedEntityName}_highest_paid.${type}`);
  };

  const buildApiUrl = (apiEndpointUrl: string, searchParameter: string ) => {
    return `${apiEndpointUrl}${searchParameter}`;
  }

  const highestPaidEmployeeSearchString = `/?function=getAllEntities&parameter=%7B%7D`;

  const fiscalYearsParam = {
  }

  const fiscalYearsEncodedParam = encodeURIComponent(JSON.stringify(fiscalYearsParam));

  const fiscalYearsSearchString = `/?function=getAvailableEntitiesByYear&parameter=${fiscalYearsEncodedParam}`;

  // Updated fetchFiscalYears function
  const fetchFiscalYears = useCallback(async (signal: AbortSignal) => {
    try {
      const response = await fetch(
        buildApiUrl(apiEndpoint, fiscalYearsSearchString),
        { signal }
      );

      if (!response.ok) return [];

      const data: any[] = await response.json();

      const years = data
        .filter(item => item && item.fiscal_year)
        .map(item => item.fiscal_year.toString());

      const sortedUniqueYears = [...new Set(years)].sort((a, b) => b.localeCompare(a));
      
      setAllFiscalYears(sortedUniqueYears);
      return sortedUniqueYears;

    } catch (err: any) {
      return [];
    }
  }, [apiEndpoint, fiscalYearsSearchString]);

  const getAllEntities = async (signal: AbortSignal): Promise<AllEntity[]> => {
    try {
      const buildApiUrl = (apiEndpointUrl: string = apiEndpoint, searchParameter: string = highestPaidEmployeeSearchString) => {
        return `${apiEndpointUrl}${searchParameter}`;
      };
      const response = await fetch(buildApiUrl(), { signal });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json()
      const entityArray = data
        .filter((e: { entity_id: number; entity_name: string | number }) => e.entity_id && e.entity_name)
        .map((e: { entity_id: number; entity_name: string | number }) => ({
          name: String(e.entity_name),
          id: e.entity_id,
        }));

      return entityArray;
    } catch (err: any) {
        if (err.name === 'AbortError') {
        } else {
        }
      return [];
    }
  };

  // Fetch all entities on mount
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    let isMounted = true;

    const fetchEntities = async () => {
      const entities = await getAllEntities(signal);
      if (!isMounted) return;

      setAllEntities(entities);
    }; 

    fetchEntities();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedEntityId || allEntities.length === 0) return;
    
    const foundEntity = allEntities.find(entity => entity.id === selectedEntityId) || null;

    if (foundEntity && foundEntity.id !== entity?.id) {
      setEntity(foundEntity);
    }
  }, [selectedEntityId, allEntities]);

  // Get fiscal year and entites for dropdown forms
  useEffect(() => {
    const controller = new AbortController();

    fetchFiscalYears(controller.signal);
    return () => {
      controller.abort();
    }
  }, [fetchFiscalYears]);

  // Initialize Tabulator
  useEffect(() => {
    if (!tableRef.current) return;

    Tabulator.registerModule([PageModule, DownloadModule, ExportModule]);

    if (!tabulatorRef.current) {
      // Initialize Tabulator instance
      tabulatorRef.current = new Tabulator(tableRef.current, {
        layout: "fitColumns",
        pagination: "local",
        paginationSize: 10,
        data: data,
        columns: [
          {
            titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-trophy'></i> Rank";
              return el;
            },
            field: "emp_rank",
            titleDownload: "Rank",
          },
          {
            titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-calendar-alt'></i> Fiscal Year";
              return el;
            },
            field: "fiscal_year",
            titleDownload: "Fiscal Year",
          },
          {
            titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-landmark'></i> Entity";
              return el;
            },
            field: "entity_name",
            titleDownload: "Entity",
          },
          {
            titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-landmark'></i> Organization";
              return el;
            },
            field: "org1",
            titleDownload: "Organization",
          },
          {
            titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-user'></i> Employee";
              return el;
            },
            field: "employee_name",
            titleDownload: "Employee",
          },
          {
            titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-briefcase'></i> Title";
              return el;
            },
            field: "title",
            titleDownload: "Title",
          },
          {
            titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-dollar-sign'></i> Wages";
              return el;
            },
            field: "wages",
            titleDownload: "Wages",
            formatter: (cell: any) => formatCurrency(cell.getValue()),
          },
          {
            titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-dollar-sign'></i> Benefits";
              return el;
            },
            field: "benefits",
            titleDownload: "Benefits",
            formatter: (cell: any) => formatCurrency(cell.getValue()),
          },
          {
            titleFormatter: () => {
              const el = document.createElement("span");
              el.innerHTML = "<i class='fas fa-dollar-sign'></i> Total";
              return el;
            },
            field: "total",
            titleDownload: "Total",
            formatter: (cell: any) => formatCurrency(cell.getValue()),
          },
        ],
      });
    } else {
      tabulatorRef.current.replaceData(data);
    }

    return () => {
      tabulatorRef.current?.destroy();
      tabulatorRef.current = null;
    };
  }, [data, tableRef, formatCurrency]);

  useEffect(() => {
    if (!entity) return;
  }, [entity]);

  return (
    <Container fluid>
      {/* Header / Jumbotron */}
        <Row className="mb-2">
          <Col>
            <Card bg="secondary" text="white" className="mx-4" style={{ border: "none", borderRadius: "0" }}>
              <Card.Body>
                <Row className="align-items-center">
                  <Col>
                    <h1 className="text-white text-center">Highest Paid Employees{' '}
                      <Button
                        size="sm"
                        variant="light"
                        onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                        aria-label="Highest Paid Employees"
                      >
                        <i className="fas fa-info"/>
                      </Button>
                    </h1>
                  </Col>
                </Row>

                <Collapse in={showHeaderInfo}>
                  <div>
                    <p className="lead mt-2 text-center">
                      To search for highest paid employees, select an entity and then a fiscal year. The top 100 highest paid employees will be returned.
                    </p>
                  </div>
                </Collapse>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Form onSubmit={(event: any) => {
          event.preventDefault();
          const found = allEntities.find(ent => ent.name === selectedEntityOption?.value);
  
          onEntityChange?.(found?.id ?? 0);
          onFiscalYearChange?.(selectedYear?.value ?? null);
        }}>
          <Row className="py-3 px-4 no-overflow-row">
            <Col md={4} style={{marginBottom: window.innerWidth > 768 ? "0px" : "10px"}} >
              <h5>
                <i className="fas fa-landmark"></i>{" "}Select an Entity
              </h5>
              <SingleSelectAsync
                value={selectedEntityOption}
                onChange={(selectedOption) => {
                  setSelectedEntities(selectedOption ? [selectedOption.value] : []);
                  const found = allEntities.find(e => e.name === selectedOption?.value) || null;
                  setEntity(found);
                }}
                isSearchable
                loadOptions={async (inputValue: string) =>
                  allEntities
                    .filter(e => e.name.toLowerCase().includes(inputValue.toLowerCase()))
                    .map(e => ({ value: e.name, label: e.name }))
                }
                defaultOptions={entityOptions}
              />
            </Col>

            <Col md={4} style={{marginBottom: window.innerWidth > 768 ? "0px" : "10px"}} >
              <h5>
                <i className="fas fa-calendar"></i>{" "}Select a Fiscal Year
              </h5>
              <SingleSelectAsync
                value={selectedYear}
                key={`year-select-${allFiscalYears.length}`} 
                onChange={(year: any) => {
                  setSelectedYear(year);
                }}
                isSearchable
                type="year"
                loadOptions={async (inputValue: string) =>
                  allFiscalYears
                    .filter(y => y.toString().includes(inputValue))
                    .map(y => ({ value: y.toString(), label: y.toString() }))
                }
                defaultOptions={allFiscalYears.map(year => ({ value: year.toString(), label: year.toString() }))}
              />
            </Col>

            <Col md={4}>
              <Button 
                variant="secondary"
                type="submit"
                style={{marginTop: window.innerWidth > 768 ? "32.5px" : "10px"}}
              >
                <i className="fas fa-paper-plane"></i>{" "}Submit
              </Button>
            </Col>
          </Row>
        </Form>

        <Row className="py-4 px-4">
          <Col>
            <div className="mb-2 d-flex gap-2">
              <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
              <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
              <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
              <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
            </div>

            <div ref={tableRef} className="table-light" />

            {!loading && data.length === 0 && (
              <div className="text-center py-3 text-muted">
                <em>No data available for the selected fiscal year.</em>
              </div>
            )}
          </Col>
        </Row>
      {/* </Card> */}
    </Container>
  );
};

export default EntityHighestPaidTableStandalone;